/**
 * useHolidays — platform_holidays 에서 공휴일 fetch + 캘린더 가상 일정 변환 hook
 *
 * Phase 1: workspace_holiday_config 가 없는 워크스페이스는 default 한국만 사용.
 * Phase 2: 워크스페이스 설정 UI 추가 + 다국가 선택 가능.
 */
import { useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import { msg } from '@lingui/macro';
import { fetchHolidays, fetchAvailableHolidayCountries } from '../../services/CalendarService';
import { CalendarEvent, PlatformHoliday, resolveHolidayCountries } from '../../models/CalendarEvent';

/** 공휴일 1건을 가상 CalendarEvent 로 변환 */
function holidayToEvent(holiday: PlatformHoliday, workspaceId: string): CalendarEvent {
  const date = new Date(`${holiday.date}T00:00:00`);
  const end = new Date(date); end.setDate(end.getDate() + 1);
  // i18n 번역 (현재 locale)
  const creatorName = i18n._(msg`Holiday`);
  return {
    id: `holiday::${holiday.country_code}::${holiday.date}`,
    workspace_id: workspaceId,
    owner_id: '__system__',
    visibility: 'public_workspace',
    owner_channel_id: null,
    shared_channel_ids: [],
    title: holiday.name,
    description: null,
    location: null,
    meeting_link: null,
    room_call_id: null,
    start_at: date.toISOString(),
    end_at: end.toISOString(),
    is_all_day: true,
    timezone: 'UTC',
    recurrence_rule: null,
    reminders: [],
    creator: { uid: '__system__', name: creatorName, photoUrl: null },
    attachments: [],
    status: 'confirmed',
    color: null, // chip 색은 eventChipStyle('holiday') 토큰으로 통일 — inline override 안 함
    cross_refs: [],
    source_post_id: null,
    source_board_id: null,
    is_auto_generated: true,
    scheduled_task_ids: null,
    comment_count: 0,
    metadata: { __holiday: true, country_code: holiday.country_code },
    created_at: date.toISOString(),
    updated_at: null,
    archived_at: null,
  };
}

interface UseHolidaysParams {
  workspaceId: string | null;
  rangeStart: Date;
  rangeEnd: Date;
  enabled: boolean;
  /** 개인이 선택한 국가 (null = 로케일 기본). per-user. */
  selectedCountries?: string[] | null;
}

export interface UseHolidaysResult {
  events: CalendarEvent[];
  /** 전체 시드 국가 풀 (개인 선택 팝오버용) */
  availableCountries: string[];
}

export function useHolidays(params: UseHolidaysParams): UseHolidaysResult {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!params.workspaceId) {
      setEvents([]);
      setAvailableCountries([]);
      return;
    }
    (async () => {
      try {
        // 전체 시드 국가 풀 (admin 설정 무관 — 공휴일은 개인 단위 선택)
        const pool: string[] = await fetchAvailableHolidayCountries().catch(() => []);
        if (!cancelled) setAvailableCountries(pool);

        // enabled=false 면 availableCountries 만 노출하고 이벤트는 비움
        if (!params.enabled) {
          if (!cancelled) setEvents([]);
          return;
        }

        // 개인 선택(null=로케일 기본) → 실제 국가. 시드 풀 안의 것만.
        const resolved = resolveHolidayCountries(params.selectedCountries ?? null, i18n.locale);
        const countryCodes = resolved.filter((c) => pool.includes(c));

        // range 가 걸치는 연도들
        const startY = params.rangeStart.getFullYear();
        const endY = params.rangeEnd.getFullYear();
        const years: number[] = [];
        for (let y = startY; y <= endY; y++) years.push(y);

        // 국가/연도 별로 fetch
        const all: PlatformHoliday[] = [];
        for (const country of countryCodes) {
          for (const year of years) {
            try {
              const list = await fetchHolidays(country, year);
              all.push(...list);
            } catch {}
          }
        }

        // range 안의 것만 필터
        const startMs = params.rangeStart.getTime();
        const endMs = params.rangeEnd.getTime();
        const filtered = all.filter((h) => {
          const t = new Date(`${h.date}T00:00:00`).getTime();
          return t >= startMs && t <= endMs;
        });

        if (cancelled) return;
        setEvents(filtered.map((h) => holidayToEvent(h, params.workspaceId!)));
      } catch (err) {
        if (!cancelled) {
          console.error('[useHolidays] fetch 실패:', err);
          setEvents([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    params.workspaceId, params.rangeStart.getTime(), params.rangeEnd.getTime(),
    params.enabled, (params.selectedCountries ?? []).join(','),
  ]);

  return { events, availableCountries };
}
